import { useEffect, useRef, useState } from "react"
import { TbChartBar } from "react-icons/tb"
import { LiquidityKey, persentage_category } from "../types/liquidityTableType"
import { BaseTable, Modal, NumberInput, TableHeader } from "components"
import { useToggle } from "utils"
import { useSaveLiquidityBudget } from "../utils/hooks/useSaveLiquidityBudget"
import { MultipleChart, MultipleChartData } from "components"


import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartOptions
} from "chart.js"

import ChartDataLabels from "chartjs-plugin-datalabels"
import annotationPlugin from "chartjs-plugin-annotation"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels,
  annotationPlugin
)

type Props = {
  list: LiquidityKey[]
  isFilterReady: boolean
  year?: number
  setList: React.Dispatch<React.SetStateAction<LiquidityKey[]>>
  locationId?: number | null
}

export default function LiquidityRatioTable({
  list,
  year,
  locationId,
  setList,
  isFilterReady
}: Props) {

  const { saveBudget } = useSaveLiquidityBudget()

  const monthKeys = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december"
  ] as const

  // Budget state per code_budget
  const [budgets, setBudgets] = useState<Record<string, string>>({})

  // Sync ketika API list berubah
  useEffect(() => {
    const initialBudgets = Object.fromEntries(
      list.map((item) => [item.code_budget, item.budget ?? ""])
    )

    setBudgets(initialBudgets)
  }, [list])

  const debounceTimer = useRef<Record<string, any>>({})

  let lastCategory = ""

  return (
    <div className="overflow-x-auto">
      <div className="max-h-[600px] overflow-y-auto">
      <BaseTable className="table table-bordered w-full">
        <TableHeader>
          <tr className="bg-black text-white text-xs">
            <th className="text-left">KEY DESCRIPTION</th>
            <th className="text-center">BUDGET</th>
            <th className="text-right">AVERAGE</th>

            {monthKeys.map((month) => (
              <th key={month} className="text-center min-w-[80px]">
                {month.toUpperCase()}
              </th>
            ))}

            <th className="text-center">ACTION</th>
          </tr>
        </TableHeader>
        <tbody>
          { list.map((item) => {
            const showCategory = item.category !== lastCategory
            lastCategory = item.category ?? ""
          
              return (
                <>
                {showCategory && (
                  <tr className="bg-base-300 font-semibold">
                    <td colSpan={16}>{item.category}</td>
                  </tr>
                )}

                <tr key={item.code_budget}>
                  <td className="font-medium !align-middle ps-10 min-w-[200px]">
                    {item.key_description}
                  </td>

                  {/* Budget */}
                  <td className="text-right">
                    <NumberInput
                      className="w-[100px]"
                      disabled={!isFilterReady}
                      textRight
                      suffix={persentage_category.includes(item.code_budget ?? "") ? " %" : ""}
                      value={budgets[item.code_budget] ?? ""}
                      onValueChange={(e, sourceInfo) => {
                        if (sourceInfo.source !== "event") return
                        const value = e.floatValue?.toString() ?? ""
                      
                        setBudgets(prev => ({
                          ...prev,
                          [item.code_budget]: value
                        }))
                      
                        // clear timer lama
                        if (debounceTimer.current[item.code_budget]) {
                          clearTimeout(debounceTimer.current[item.code_budget])
                        }
                      
                        debounceTimer.current[item.code_budget] = setTimeout(async () => {
                          if (!year || !value) return
                      
                          const result = await saveBudget({
                            budget_id: item.budget_id,
                            code_budget: item.code_budget,
                            year,
                            budget: value,
                            ...(locationId != null
                              ? { location_id: locationId }
                              : {})
                          })
                      
                          if (!result) return
                      
                          const newBudgetId =
                            result.cr_budget_loc_id ??
                            result.cr_budget_global_id
                      
                            setList(prev =>
                              prev.map(row =>
                                row.code_budget === item.code_budget
                                  ? {
                                      ...row,
                                      budget_id: newBudgetId ?? row.budget_id,
                                      budget: value
                                    }
                                  : row
                              )
                            )
                      
                        }, 2000)
                      }}
                    />
                  </td>
                

                  {/* Average */}
                  <td className="text-right !align-middle">
                    {item.average ?? ""}
                    {item.average && persentage_category.includes(item.code_budget ?? "") ? " %" : ""}
                  </td>

                  {/* Months */}
                  {monthKeys.map((month) => (
                    <td className="align-middle">
                      <div className="flex items-center justify-end h-10">
                        {item[month] ?? ""}
                        {item[month] && persentage_category.includes(item.code_budget ?? "") ? " %" : ""}
                      </div>
                    </td>
                  ))}

                  {/* Action */}
                  <td className="text-center !align-middle">
                    <ChartSection
                      disabled={!isFilterReady}
                      headerList={[
                        "BUDGET",
                        "JANUARY",
                        "FEBRUARY",
                        "MARCH",
                        "APRIL",
                        "MAY",
                        "JUNE",
                        "JULY",
                        "AUGUST",
                        "SEPTEMBER",
                        "OCTOBER",
                        "NOVEMBER",
                        "DECEMBER"
                      ]}
                      list={[
                        Number(item.budget),
                        Number(item.january??0),
                        Number(item.february??0),
                        Number(item.march??0),
                        Number(item.april??0),
                        Number(item.may??0),
                        Number(item.june??0),
                        Number(item.july??0),
                        Number(item.august??0),
                        Number(item.september??0),
                        Number(item.october??0),
                        Number(item.november??0),
                        Number(item.december??0),
                        Number(item.average??0)
                      ]}
                      title={item.key_description}
                      dataItem={item}
                    />
                  </td>
                </tr>
                </>
              )
            }
          )}
        </tbody>
      </BaseTable>
      </div>
    </div>
  )
}


function ChartSection(params: {
  headerList: string[]
  list: (number | null | undefined)[]
  title: string
  disabled?: boolean
  dataItem?:LiquidityKey
}) {

  const { isActive, toggle } = useToggle(false)
  const [chartType, setChartType] = useState<"line" | "bar">("line")

  const labels = params.headerList.slice(1)

  const budget = Number(params.list[0] ?? 0)
  const average = Number(params.list[13] ?? 0)

  const actualValues = labels.map((_, index) =>
    Number(params.list[index + 1] ?? 0)
  )

  // const values = [...actualValues, budget]

  // const minValue = Math.min(...values)
  // const maxValue = Math.max(...values)

  const data: MultipleChartData = {
    labels,
    datasets: [
      {
        label: "Actual",
        data: actualValues,
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59,130,246,0.2)",
        borderWidth: 2,
        fill: false
      }
    ]
  }

  const options: ChartOptions<"bar"> = {

    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 20
    },
  
    scales: {
      x: {
        position: chartType === "line" ? "bottom" : "center",
    
        ticks: {
          display: chartType === "line"
        },
    
        grid: { display: false },
        border: { display: false }
      },
    
      y: {
        grid: {
          color: (ctx) =>
            ctx.tick.value === 0
              ? "rgb(0,0,0)"
              : "rgba(0,0,0,0)"
        },
        border: {
          display: false
        }
      }
    },
      
  
    plugins: {
      legend: { display: false },
  
      datalabels:  chartType === "bar" ? {
        clip:false,
        labels: {
      
          month: {
            color: "#666",
      
            formatter: (_, context) => {
              return labels[context.dataIndex]
            },
      
            anchor: (context) => {
              const value = context.dataset.data[context.dataIndex] as number
              return value > 0 ? "start" : "end"
            },
      
            align: (context) => {
              const value = context.dataset.data[context.dataIndex] as number
              return value > 0 ? "bottom" : "top"
            }
          },
      
          value: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
            color: "#000",
            font: {
              weight: "bold"
            },
      
            formatter: (value: number) => {
              return value.toFixed(2)
            },
      
            anchor: (context) => {
              const value = context.dataset.data[context.dataIndex] as number
              return value < 0 ? "start" : "end"
            },
      
            align: (context) => {
              const value = context.dataset.data[context.dataIndex] as number
              return value < 0 ? "bottom" : "top"
            }
          }
      
        }
      }
      :{display:false},
        
  
      annotation: {
        annotations: {
  
          budgetLine: {
            type: "line",
            yMin: budget,
            yMax: budget,
            borderColor: "#DC2626",
            borderWidth: 2,
            borderDash: [6,6],
  
            label: {
              display: true,
              content: `BUDGET (${budget.toFixed(2)})`,
              position: "end",
              color: "#DC2626",
              font: { weight: "bold" },
              yAdjust: -10,
              backgroundColor: "transparent"
            }
          },
  
          averageLine: {
            type: "line",
            yMin: average,
            yMax: average,
            borderColor: "#ff9d00",
            borderWidth: 2,
            borderDash: [6,6],
  
            label: {
              display: true,
              content: `AVERAGE (${average.toFixed(2)})`,
              position: "end",
              color: "#ff9d00",
              font: { weight: "bold" },
              yAdjust: -10,
              backgroundColor: "transparent"
            }
          }
  
        }
      }
  
    }
  
  }

  return (
    <>
      <button
        onClick={toggle}
        disabled={params.disabled}
        className="btn btn-xs btn-warning text-white"
      >
        <TbChartBar size={16} />
      </button>

      {isActive && (
        <Modal
          isOpen
          title={`${params.title.toUpperCase()} CHART`}
          closeModal={toggle}
          isdarktitle
        >
          <Modal.Body>
            <div className="flex justify-end mb-2">
              <select
                className="select select-sm select-bordered"
                value={chartType}
                onChange={(e) => setChartType(e.target.value as "line" | "bar")}
              >
                <option value="line">Line</option>
                <option value="bar">Bar</option>
              </select>
            </div>

            <div style={{ height: 400 }}>
              <MultipleChart
                className="min-h-[400px]"
                chartType={chartType as "bar"}
                data={data}
                options={options}
              />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
  
              {/* FORMULA */}
              <div className="border-2 border-gray-400 min-h-[300px]rounded-lg p-4">
                <h2 className="font-bold mb-3">FORMULA</h2>

                <div className="text-sm space-y-2">
                    <div
                        dangerouslySetInnerHTML={{ __html: params.dataItem?.formula ?? "" }}
                    />
                </div>
              </div>

              {/* AI ANALYTIC */}
              <div className="border-2 border-gray-400 min-h-[300px] rounded-lg p-4">
                <h2 className="font-bold mb-3">AI ANALYTIC</h2>

                <div className="text-sm text-gray-600">
                  
                </div>
              </div>

            </div>

          </Modal.Body>
        </Modal>
      )}
    </>
  )
}